import { Button, ErrorBox, Link, navigate, useSearch } from '@elsa/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BusyButton, RadarLoading } from '../app/loading.tsx';
import { rememberOrg, useAuthSession } from '../lib/session.tsx';
import { rpc, supabase } from '../lib/supabase.ts';
import { AuthCard } from './Auth.tsx';

// Linkul din emailul de invitatie: /invitatie?token=… Daca nu esti logat, intri (sau iti faci
// cont cu aceeasi adresa) si revii aici.
interface Info {
  organization: string;
  role: string;
  counties?: string[];
  job_title: string | null;
  email: string;
  expired: boolean;
  accepted: boolean;
  for_me: boolean;
}

export function InvitePage() {
  const token = useSearch().get('token') ?? '';
  const session = useAuthSession();
  const here = `/invitatie?token=${encodeURIComponent(token)}`;
  const info = useQuery({
    queryKey: ['invitation', token, session?.user.id],
    enabled: Boolean(session && token),
    queryFn: () => rpc<Info | null>('invitation_info', { p_token: token }),
  });
  const accept = useMutation({
    mutationFn: () => rpc<string>('accept_invitation', { p_token: token }),
    onSuccess: (org) => {
      rememberOrg(org);
      navigate('/app');
    },
  });

  if (!token)
    return (
      <AuthCard title="Invitație">Linkul nu e complet. Deschide-l din nou din email.</AuthCard>
    );
  if (session === undefined) {
    return (
      <AuthCard title="Invitație">
        <RadarLoading>Verificăm sesiunea…</RadarLoading>
      </AuthCard>
    );
  }
  if (!session) {
    return (
      <AuthCard title="Ai fost invitat în Elsa">
        <p className="text-sm">
          Intră în cont sau creează unul nou cu adresa pe care ai primit invitația.
        </p>
        <div className="flex gap-2">
          <Link
            to={`/autentificare?next=${encodeURIComponent(here)}`}
            className="flex-1 border-2 border-semnal bg-semnal px-3 py-2 text-center text-sm font-bold text-cod hover:border-semnal-600 hover:bg-semnal-600"
          >
            Intră
          </Link>
          <Link
            to={`/autentificare?mod=inregistrare&next=${encodeURIComponent(here)}`}
            className="flex-1 border-2 border-cerneala px-3 py-2 text-center text-sm font-bold hover:bg-cerneala/5"
          >
            Cont nou
          </Link>
        </div>
      </AuthCard>
    );
  }
  if (info.isLoading) {
    return (
      <AuthCard title="Invitație">
        <RadarLoading>Se încarcă invitația…</RadarLoading>
      </AuthCard>
    );
  }
  const i = info.data;
  if (!i)
    return <AuthCard title="Invitație">Invitația nu există. Cere colegului una nouă.</AuthCard>;
  if (i.accepted) {
    return (
      <AuthCard title="Invitație acceptată">
        <Link to="/app" className="text-sm underline">
          Deschide aplicația
        </Link>
      </AuthCard>
    );
  }
  if (i.expired)
    return (
      <AuthCard title="Invitația a expirat">Cere colegului din {i.organization} una nouă.</AuthCard>
    );
  if (!i.for_me) {
    return (
      <AuthCard title="Invitația e pentru altă adresă">
        <p className="text-sm">
          Invitația e pentru <b>{i.email}</b>, iar tu ești în cont cu <b>{session.user.email}</b>.
        </p>
        <Button className="w-full" onClick={() => void supabase.auth.signOut()}>
          Ieși și intră cu {i.email}
        </Button>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={`Intră în echipa ${i.organization}`}>
      <p className="text-sm">
        Rolul tău: <b>{i.role}</b>
        {i.counties?.length ? ` · județe: ${i.counties.join(', ')}` : ''}
        {i.job_title ? ` (${i.job_title})` : ''}.
      </p>
      <ErrorBox error={accept.error} />
      <BusyButton
        tone="primary"
        className="w-full"
        busy={accept.isPending}
        onClick={() => accept.mutate()}
      >
        Acceptă invitația
      </BusyButton>
    </AuthCard>
  );
}
